import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import TrainerPromoProfileComponent from '../TrainerManage/TrainerPromoProfileComponent';
import { getCoachPromoProfilesAPI } from '../../api/coachPromo';
import { Body2Regular, Body3Regular, CaptionLight, Head3, Subtitle1Bold } from '../../lib/font';
import { colors } from '../../lib/colors';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';

type PromoProfile = {
    coachId: string;
    coachName: string;
    coachProfileImageUrl?: string;
    representativeImageUrl?: string;
    displayImageUrl?: string;
    headline?: string;
    tags?: string[];
    shortIntro?: string;
    isVisible?: boolean;
    sortOrder?: number;
};

const KIOSK_URL = 'https://kiosk.rappomanager.com';

const TrainerPromoSettingsPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const centerId = authObject?.centerId;
    const [selectedCoachId, setSelectedCoachId] = useState('');
    const [copied, setCopied] = useState(false);
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_trainer_promo') !== 'hidden');
    const [showAccessGuide, setShowAccessGuide] = useState(() => localStorage.getItem('guide_trainer_promo_access') === 'expanded');

    const profilesQuery = useQuery(
        ['coachPromoProfiles', centerId],
        () => getCoachPromoProfilesAPI(centerId, authorization),
        {
            enabled: !!centerId && !!authorization,
        }
    );

    const profiles = useMemo<PromoProfile[]>(() => {
        const list = profilesQuery.data?.data?.profiles || [];
        return [...list].sort(
            (a: PromoProfile, b: PromoProfile) =>
                Number(a.sortOrder || 0) - Number(b.sortOrder || 0) ||
                String(a.coachName || '').localeCompare(String(b.coachName || ''), 'ko')
        );
    }, [profilesQuery.data]);

    const selectedProfile = profiles.find((profile) => profile.coachId === selectedCoachId) || profiles[0];
    const visibleProfiles = profiles.filter((profile) => profile.isVisible);

    useEffect(() => {
        if (profiles.length === 0) return;
        if (!selectedCoachId || !profiles.some((profile) => profile.coachId === selectedCoachId)) {
            setSelectedCoachId(profiles[0].coachId);
        }
    }, [profiles, selectedCoachId]);

    const handleCopyKioskUrl = async () => {
        try {
            await navigator.clipboard.writeText(KIOSK_URL);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            createAlert('복사 실패', '태블릿 접속 주소 복사에 실패했습니다.', true);
        }
    };

    const cardStyle: React.CSSProperties = {
        backgroundColor: colors.white,
        border: '1px solid #E5E7EB',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
    };

    return (
        <Col
            style={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#F8FAFC',
                marginTop: 64,
                overflow: 'auto',
            }}
        >
            <Col style={{ width: '100%', maxWidth: 1440, margin: '0 auto', padding: '40px 32px 56px' }}>
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <Subtitle1Bold>태블릿 홍보</Subtitle1Bold>
                    {!showGuide && (
                        <button
                            onClick={() => { setShowGuide(true); localStorage.removeItem('guide_trainer_promo'); }}
                            style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #FDBA74', backgroundColor: '#FFF7ED', color: '#EA580C', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >?</button>
                    )}
                </Row>
                <CaptionLight color={colors.LAVEL_1} style={{ marginTop: 6 }}>
                    강사 홍보판에 노출할 강사 프로필과 순서를 구성합니다
                </CaptionLight>

                {showGuide && (
                    <Col style={{ marginTop: 12, padding: '18px 22px', backgroundColor: '#FFF7ED', borderRadius: 12, border: '1px solid #FDBA74', position: 'relative' }}>
                        <button
                            onClick={() => { setShowGuide(false); localStorage.setItem('guide_trainer_promo', 'hidden'); }}
                            style={{ position: 'absolute', top: 10, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#FDBA74', cursor: 'pointer' }}
                        >✕</button>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#9A3412', marginBottom: 6 }}>태블릿 홍보가 뭔가요?</span>
                        <Col style={{ gap: 3, fontSize: 13, color: '#9A3412', lineHeight: 1.7 }}>
                            <span>센터에 비치한 <b>태블릿(또는 PC)에서 회원에게 강사를 소개</b>하는 홍보판 기능입니다.</span>
                            <span>방문하신 분이 강사 프로필을 보고 직접 PT/레슨을 신청할 수 있도록 도와줍니다.</span>
                        </Col>
                    </Col>
                )}

                {/* 태블릿 접속 안내 */}
                <Col
                    style={{
                        marginTop: 24,
                        padding: '22px 24px',
                        background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%)',
                        borderRadius: 14,
                        border: '1px solid #BFDBFE',
                    }}
                >
                    <Row
                        style={{ alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                        onClick={() => {
                            const next = !showAccessGuide;
                            setShowAccessGuide(next);
                            if (next) {
                                localStorage.setItem('guide_trainer_promo_access', 'expanded');
                            } else {
                                localStorage.removeItem('guide_trainer_promo_access');
                            }
                        }}
                    >
                        <Row style={{ alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 20 }}>📱</span>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#1E3A8A' }}>
                                태블릿 홍보판 접속 방법
                            </span>
                        </Row>
                        <Row style={{ alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 600 }}>
                                {showAccessGuide ? '접기' : '펼치기'}
                            </span>
                            <span
                                style={{
                                    fontSize: 12,
                                    color: '#1D4ED8',
                                    transform: showAccessGuide ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.15s',
                                    display: 'inline-block',
                                }}
                            >
                                ▾
                            </span>
                        </Row>
                    </Row>
                    {showAccessGuide && (
                    <>
                    <span style={{ fontSize: 13, color: '#1E40AF', marginTop: 8, lineHeight: 1.65 }}>
                        센터에 비치한 태블릿(또는 PC)의 브라우저에서 아래 주소로 접속하시면 강사 홍보판을 바로 띄울 수 있어요. 별도 앱 설치 없이 웹으로 바로 동작합니다.
                    </span>

                    {/* URL 박스 */}
                    <Row
                        style={{
                            marginTop: 14,
                            alignItems: 'center',
                            padding: '12px 14px',
                            backgroundColor: 'white',
                            borderRadius: 10,
                            border: '1px solid #BFDBFE',
                            gap: 10,
                            boxShadow: '0 1px 3px rgba(30,64,175,0.06)',
                        }}
                    >
                        <span
                            style={{
                                flex: 1,
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#1D4ED8',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                wordBreak: 'break-all',
                            }}
                        >
                            {KIOSK_URL}
                        </span>
                        <button
                            type="button"
                            onClick={handleCopyKioskUrl}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 7,
                                fontSize: 12,
                                fontWeight: 600,
                                backgroundColor: copied ? '#DCFCE7' : '#FFFFFF',
                                color: copied ? '#166534' : '#1D4ED8',
                                border: `1px solid ${copied ? '#86EFAC' : '#BFDBFE'}`,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.15s',
                            }}
                        >
                            {copied ? '복사됨 ✓' : '주소 복사'}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.open(KIOSK_URL, '_blank')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 7,
                                fontSize: 12,
                                fontWeight: 600,
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                border: '1px solid #3B82F6',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            새 창 열기 ↗
                        </button>
                    </Row>

                    {/* 단계별 안내 */}
                    <Col style={{ marginTop: 18, gap: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1E3A8A' }}>이렇게 사용하세요</span>
                        {([
                            {
                                num: '1',
                                title: '크롬(Chrome) 브라우저로 접속 (권장)',
                                desc: (
                                    <>
                                        위 주소를 그대로 입력하거나 "주소 복사" 버튼으로 복사해서 붙여넣어 주세요.
                                        <br />
                                        안정적인 동작을 위해 <b>크롬(Chrome) 브라우저 사용을 권장</b>드려요. 사파리 · 엣지 등 다른 브라우저도 사용 가능합니다.
                                    </>
                                ),
                            },
                            {
                                num: '2',
                                title: '지금 로그인하신 관리자 계정으로 로그인',
                                desc: (
                                    <>
                                        관리자 페이지와 <b>동일한 아이디 · 비밀번호</b>를 그대로 입력하시면 됩니다. 별도의 태블릿 전용 계정은 필요 없어요.
                                        {authObject?.adminId && (
                                            <Row style={{ marginTop: 8, alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 11, color: '#6B7280' }}>현재 로그인 아이디 :</span>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: '#1D4ED8',
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                        backgroundColor: '#EFF6FF',
                                                        border: '1px solid #BFDBFE',
                                                        padding: '2px 8px',
                                                        borderRadius: 6,
                                                    }}
                                                >
                                                    {authObject.adminId}
                                                </span>
                                            </Row>
                                        )}
                                    </>
                                ),
                            },
                            {
                                num: '3',
                                title: '강사 홍보판 화면으로 이동',
                                desc: (
                                    <>
                                        로그인 후 <b>"강사 홍보판"</b> 메뉴를 선택하면, 이 페이지에서 노출 설정한 강사들이 태블릿에 표시됩니다.
                                        <br />
                                        오른쪽 미리보기로 회원이 보게 될 화면을 미리 확인하실 수 있어요.
                                    </>
                                ),
                            },
                            {
                                num: '4',
                                title: '센터에 태블릿 비치 후 회원에게 안내',
                                desc: (
                                    <>
                                        대기 공간이나 상담 데스크에 태블릿을 두시면, 회원이 강사 프로필을 보고 마음에 드는 강사에게 <b>바로 신청</b>할 수 있어요.
                                    </>
                                ),
                            },
                        ] as { num: string; title: string; desc: React.ReactNode }[]).map((step) => (
                            <Row key={step.num} style={{ gap: 12, alignItems: 'flex-start' }}>
                                <span
                                    style={{
                                        flexShrink: 0,
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        backgroundColor: '#3B82F6',
                                        color: 'white',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: 1,
                                    }}
                                >
                                    {step.num}
                                </span>
                                <Col style={{ flex: 1, gap: 2 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>{step.title}</span>
                                    <span style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.65 }}>{step.desc}</span>
                                </Col>
                            </Row>
                        ))}
                    </Col>

                    {/* 태블릿 홈 화면 추가 안내 */}
                    <Col
                        style={{
                            marginTop: 16,
                            padding: '14px 16px',
                            backgroundColor: '#FAF5FF',
                            borderRadius: 10,
                            border: '1px solid #DDD6FE',
                            gap: 10,
                        }}
                    >
                        <Row style={{ alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 14 }}>📌</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#6D28D9' }}>
                                태블릿에서 홈 화면 아이콘으로 만들기 (추천)
                            </span>
                        </Row>
                        <span style={{ fontSize: 12, color: '#5B21B6', lineHeight: 1.65 }}>
                            매번 주소를 입력할 필요 없이 앱처럼 한 번에 열 수 있어요.
                        </span>
                        <Col style={{ gap: 6, marginTop: 2 }}>
                            {[
                                <>태블릿 Chrome에서 <b>https://kiosk.rappomanager.com</b> 으로 접속</>,
                                <>주소창 옆 <b>공유 버튼</b> → <b>"Add to home screen" (홈 화면에 추가)</b> 선택</>,
                                <>이제부터는 Chrome 앱이 아니라 <b>홈 화면에 생긴 "라포 키오스크" 아이콘</b>으로 실행하세요</>,
                            ].map((desc, idx) => (
                                <Row key={idx} style={{ gap: 8, alignItems: 'flex-start' }}>
                                    <span
                                        style={{
                                            flexShrink: 0,
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            backgroundColor: '#8B5CF6',
                                            color: 'white',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: 1,
                                        }}
                                    >
                                        {idx + 1}
                                    </span>
                                    <span style={{ flex: 1, fontSize: 12, color: '#4C1D95', lineHeight: 1.65 }}>{desc}</span>
                                </Row>
                            ))}
                        </Col>
                        <span style={{ fontSize: 11, color: '#7C3AED', backgroundColor: '#F3E8FF', borderRadius: 6, padding: '6px 8px', lineHeight: 1.55 }}>
                            ⚠️ Chrome 앱으로 열면 주소창·탭 같은 브라우저 UI가 보일 수 있어요. <b>꼭 홈 화면 아이콘으로 실행</b>해주세요.
                        </span>
                    </Col>

                    {/* 팁 */}
                    <Col
                        style={{
                            marginTop: 12,
                            padding: '12px 14px',
                            backgroundColor: '#FFFFFF',
                            borderRadius: 10,
                            border: '1px dashed #93C5FD',
                            gap: 6,
                        }}
                    >
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1E40AF' }}>💡 꿀팁</span>
                        <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.65 }}>
                            · 노출 순서 · 대표 이미지 · 소개 문구는 <b>왼쪽 강사 목록에서 강사를 선택</b>해 바로 수정할 수 있어요.<br />
                            · 인터넷 연결이 끊기면 화면이 정상 표시되지 않으니 와이파이를 안정적으로 연결해 주세요.
                        </span>
                    </Col>
                    </>
                    )}
                </Col>

                <Row style={{ marginTop: 24, gap: 20, alignItems: 'stretch' }}>
                    <Col style={{ ...cardStyle, width: 280, padding: 18, gap: 12 }}>
                        <Head3 className="text-LAVEL_4">강사 목록</Head3>
                        <Body3Regular className="text-LAVEL_2">
                            노출 여부와 순서를 한 곳에서 확인합니다.
                        </Body3Regular>

                        {profilesQuery.isLoading ? (
                            <Body2Regular color={colors.LAVEL_1} style={{ marginTop: 20 }}>
                                강사 정보를 불러오는 중...
                            </Body2Regular>
                        ) : profiles.length === 0 ? (
                            <Body2Regular color={colors.LAVEL_1} style={{ marginTop: 20 }}>
                                등록된 강사가 없습니다.
                            </Body2Regular>
                        ) : (
                            <Col style={{ gap: 8, marginTop: 8 }}>
                                {profiles.map((profile) => {
                                    const isSelected = profile.coachId === selectedProfile?.coachId;
                                    return (
                                        <button
                                            key={profile.coachId}
                                            type="button"
                                            onClick={() => setSelectedCoachId(profile.coachId)}
                                            style={{
                                                width: '100%',
                                                padding: 10,
                                                borderRadius: 10,
                                                border: isSelected ? '1px solid #3B82F6' : '1px solid #E5E7EB',
                                                backgroundColor: isSelected ? '#EFF6FF' : colors.white,
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <Row style={{ gap: 10, alignItems: 'center' }}>
                                                <div
                                                    style={{
                                                        width: 42,
                                                        height: 42,
                                                        borderRadius: 8,
                                                        overflow: 'hidden',
                                                        backgroundColor: '#EEF2F7',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {profile.displayImageUrl ? (
                                                        <img
                                                            src={profile.displayImageUrl}
                                                            alt=""
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    ) : null}
                                                </div>
                                                <Col style={{ minWidth: 0, flex: 1 }}>
                                                    <Row style={{ gap: 6, alignItems: 'center' }}>
                                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                                                            {profile.coachName || '이름 없음'}
                                                        </span>
                                                        <span
                                                            style={{
                                                                fontSize: 10,
                                                                fontWeight: 700,
                                                                color: profile.isVisible ? '#166534' : '#6B7280',
                                                                backgroundColor: profile.isVisible ? '#DCFCE7' : '#F3F4F6',
                                                                borderRadius: 999,
                                                                padding: '2px 6px',
                                                            }}
                                                        >
                                                            {profile.isVisible ? '노출' : '숨김'}
                                                        </span>
                                                    </Row>
                                                    <span
                                                        style={{
                                                            marginTop: 3,
                                                            fontSize: 12,
                                                            color: '#6B7280',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        순서 {Number(profile.sortOrder || 0)}
                                                        {profile.headline ? ` · ${profile.headline}` : ''}
                                                    </span>
                                                </Col>
                                            </Row>
                                        </button>
                                    );
                                })}
                            </Col>
                        )}
                    </Col>

                    <Col style={{ flex: 1, minWidth: 0 }}>
                        {selectedProfile ? (
                            <TrainerPromoProfileComponent
                                centerId={centerId}
                                coachId={selectedProfile.coachId}
                                coachName={selectedProfile.coachName || ''}
                                fallbackImageUrl={selectedProfile.coachProfileImageUrl}
                                createAlert={createAlert}
                                fluid
                                onSaved={() => profilesQuery.refetch()}
                            />
                        ) : (
                            <Col style={{ ...cardStyle, padding: 40, minHeight: 420, justifyContent: 'center', alignItems: 'center' }}>
                                <Body2Regular color={colors.LAVEL_1}>편집할 강사를 선택해주세요.</Body2Regular>
                            </Col>
                        )}
                    </Col>

                    <TabletPreview profiles={visibleProfiles} selectedCoachId={selectedProfile?.coachId || ''} />
                </Row>
            </Col>

            <div style={{ width: '100%', marginTop: 20 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

const TabletPreview = ({ profiles, selectedCoachId }: { profiles: PromoProfile[]; selectedCoachId: string }) => {
    const selectedVisibleProfile =
        profiles.find((profile) => profile.coachId === selectedCoachId) || profiles[0] || null;

    return (
        <Col
            style={{
                width: 330,
                flexShrink: 0,
                backgroundColor: '#111827',
                borderRadius: 24,
                border: '10px solid #1F2937',
                padding: 16,
                minHeight: 650,
                boxShadow: '0 18px 48px rgba(15, 23, 42, 0.28)',
            }}
        >
            <Row style={{ justifyContent: 'center', marginBottom: 14 }}>
                <div style={{ width: 48, height: 4, backgroundColor: '#4B5563', borderRadius: 999 }} />
            </Row>
            <Col style={{ gap: 14 }}>
                <Col>
                    <span style={{ color: '#CBD5E1', fontSize: 12 }}>나에게 맞는 운동 전문가 찾기</span>
                    <span style={{ color: colors.white, fontSize: 22, fontWeight: 800, marginTop: 4 }}>강사 홍보판</span>
                </Col>

                <Col style={{ gap: 10 }}>
                    {profiles.length === 0 ? (
                        <Col
                            style={{
                                height: 160,
                                borderRadius: 14,
                                backgroundColor: '#1F2937',
                                border: '1px solid #374151',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 18,
                            }}
                        >
                            <span style={{ color: '#CBD5E1', fontSize: 13, textAlign: 'center', lineHeight: 1.6 }}>
                                노출 중인 강사가 없습니다.
                            </span>
                        </Col>
                    ) : (
                        profiles.slice(0, 4).map((profile) => (
                            <Row
                                key={profile.coachId}
                                style={{
                                    gap: 10,
                                    padding: 10,
                                    borderRadius: 14,
                                    backgroundColor:
                                        profile.coachId === selectedVisibleProfile?.coachId ? '#293A68' : '#1F2937',
                                    border:
                                        profile.coachId === selectedVisibleProfile?.coachId
                                            ? '1px solid #60A5FA'
                                            : '1px solid #374151',
                                }}
                            >
                                <div
                                    style={{
                                        width: 54,
                                        height: 64,
                                        borderRadius: 10,
                                        backgroundColor: '#334155',
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                    }}
                                >
                                    {profile.displayImageUrl ? (
                                        <img
                                            src={profile.displayImageUrl}
                                            alt=""
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : null}
                                </div>
                                <Col style={{ minWidth: 0, flex: 1 }}>
                                    <span style={{ color: colors.white, fontSize: 14, fontWeight: 800 }}>
                                        {profile.coachName}
                                    </span>
                                    <span
                                        style={{
                                            color: '#CBD5E1',
                                            fontSize: 11,
                                            lineHeight: 1.45,
                                            marginTop: 3,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {profile.headline || '강사 소개를 입력해주세요.'}
                                    </span>
                                    <Row style={{ gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                                        {(profile.tags || []).slice(0, 2).map((tag) => (
                                            <span
                                                key={tag}
                                                style={{
                                                    color: '#A5B4FC',
                                                    backgroundColor: '#312E81',
                                                    borderRadius: 999,
                                                    padding: '2px 6px',
                                                    fontSize: 10,
                                                }}
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </Row>
                                </Col>
                            </Row>
                        ))
                    )}
                </Col>

                {selectedVisibleProfile && (
                    <Col
                        style={{
                            marginTop: 4,
                            padding: 14,
                            borderRadius: 16,
                            backgroundColor: '#F8FAFC',
                            gap: 10,
                        }}
                    >
                        <span style={{ color: '#111827', fontSize: 12, fontWeight: 800 }}>상세 화면 미리보기</span>
                        <Row style={{ gap: 10 }}>
                            <div
                                style={{
                                    width: 78,
                                    height: 96,
                                    borderRadius: 12,
                                    backgroundColor: '#E5E7EB',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                }}
                            >
                                {selectedVisibleProfile.displayImageUrl ? (
                                    <img
                                        src={selectedVisibleProfile.displayImageUrl}
                                        alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : null}
                            </div>
                            <Col style={{ minWidth: 0, flex: 1 }}>
                                <span style={{ color: '#111827', fontSize: 16, fontWeight: 800 }}>
                                    {selectedVisibleProfile.coachName}
                                </span>
                                <span style={{ color: '#475569', fontSize: 11, lineHeight: 1.55, marginTop: 6 }}>
                                    {selectedVisibleProfile.shortIntro || selectedVisibleProfile.headline || '소개 문구를 입력해주세요.'}
                                </span>
                            </Col>
                        </Row>
                        <button
                            type="button"
                            style={{
                                height: 38,
                                borderRadius: 10,
                                border: 'none',
                                backgroundColor: '#4F46E5',
                                color: colors.white,
                                fontWeight: 800,
                            }}
                        >
                            신청하기
                        </button>
                    </Col>
                )}
            </Col>
        </Col>
    );
};

export default TrainerPromoSettingsPage;
